import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, Search, Calendar as CalendarIcon, Plus, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useAdmin } from '@/contexts/AdminContext';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import { cn } from '@/lib/utils';
import SiteFilter from '@/components/admin/SiteFilter';
import BookingDetail from '@/components/admin/BookingDetail';

const STATUS_STYLES = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  no_show: 'bg-red-100 text-red-800 border-red-300',
};

const DATE_PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
];

function getDateRange(preset) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return { from: startOfToday, to: null };
    case 'week': {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfWeek.getDate() - ((startOfToday.getDay() + 6) % 7));
      return { from: startOfWeek, to: null };
    }
    case 'month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: null };
    default:
      return { from: null, to: null };
  }
}

export default function AdminBookings() {
  const { apiFetch } = useAdmin();
  const { createDraft } = useBookingDraft();
  const [siteFilter, setSiteFilter] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Load providers for filter
  useEffect(() => {
    apiFetch('/providers').then(r => r.json()).then(d => setProviders(d.providers || [])).catch(() => {});
  }, [apiFetch]);

  const loadData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    params.set('take', '200');
    if (siteFilter !== 'all') params.set('siteKey', siteFilter);
    if (providerFilter !== 'all') params.set('providerId', providerFilter);
    apiFetch(`/bookings?${params}`).then(r => r.json()).then(d => setBookings(d.items || [])).catch(() => {}).finally(() => { if (!silent) setLoading(false); });
  }, [apiFetch, siteFilter, providerFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const dateRange = getDateRange(datePreset);

  const filtered = bookings.filter(b => {
    const matchStatus = statusFilter === 'all' || b.status === statusFilter;
    const matchSearch = !search || `${b.patient?.firstName} ${b.patient?.lastName} ${b.reference} ${b.service}`.toLowerCase().includes(search.toLowerCase());
    let matchDate = true;
    if (dateRange.from) {
      const bookingDate = new Date(b.confirmedDate || b.createdAt);
      matchDate = bookingDate >= dateRange.from;
    }
    return matchStatus && matchSearch && matchDate;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Operations</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Bookings</h2>
          <p className="text-stone-500 text-sm mt-1">{filtered.length} booking{filtered.length !== 1 ? 's' : ''}{datePreset !== 'all' ? ` (${DATE_PRESETS.find(d => d.key === datePreset)?.label})` : ''}</p>
        </div>
        <Button onClick={() => createDraft()} className="h-10 bg-stone-900 text-white hover:bg-stone-800 gap-2 shrink-0">
          <Plus className="w-4 h-4" /> New Booking
        </Button>
      </div>

      {/* Filters Row 1: Search + Site + Provider */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
          <Input placeholder="Search patient, reference, service..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white border-stone-200 h-10" />
        </div>
        <SiteFilter value={siteFilter} onChange={setSiteFilter} />
        <select
          value={providerFilter}
          onChange={e => setProviderFilter(e.target.value)}
          className="h-10 rounded-lg border border-stone-200 bg-white text-sm px-3 text-stone-700"
        >
          <option value="all">All Providers</option>
          {providers.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Filters Row 2: Date Presets + Status */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex gap-1.5">
          {DATE_PRESETS.map(d => (
            <button
              key={d.key}
              onClick={() => setDatePreset(d.key)}
              className={cn(
                'text-xs font-medium px-3 py-2 rounded-lg border transition-colors',
                datePreset === d.key ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
              )}
            >
              {d.label}
            </button>
          ))}
        </div>
        <div className="h-5 w-px bg-stone-200 hidden sm:block" />
        <div className="flex gap-1.5 flex-wrap">
          {['all', 'confirmed', 'pending', 'completed', 'cancelled', 'no_show'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={cn('text-xs font-medium px-3 py-2 rounded-lg border transition-colors capitalize', statusFilter === s ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50')}>
              {s === 'all' ? 'All Status' : s.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>
      
      <Card className="border-stone-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <CardContent className="py-16 text-center"><ClipboardList className="w-12 h-12 text-stone-200 mx-auto mb-4" /><p className="text-stone-600 font-medium">No bookings found</p><p className="text-xs text-stone-400 mt-1">Try adjusting your filters.</p></CardContent>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-100 bg-stone-50/50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Reference</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Patient</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Service</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Provider</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Payment</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Total</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Date</th>
              </tr></thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((b, i) => (
                  <motion.tr 
                    key={b.id} 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    transition={{ delay: Math.min(i * 0.01, 0.3) }} 
                    className="hover:bg-stone-50/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedBooking(b.id)}
                  >
                    <td className="px-5 py-3.5"><span className="font-mono text-xs font-semibold text-stone-900">{b.reference}</span></td>
                    <td className="px-5 py-3.5"><p className="font-medium text-stone-900 text-sm">{b.patient?.firstName} {b.patient?.lastName}</p></td>
                    <td className="px-5 py-3.5 hidden md:table-cell"><span className="text-stone-600 text-sm truncate max-w-[180px] block">{b.service}</span></td>
                    <td className="px-5 py-3.5 hidden lg:table-cell"><span className="text-xs text-stone-500">{b.provider?.name || '—'}</span></td>
                    <td className="px-5 py-3.5"><span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold border capitalize', STATUS_STYLES[b.status] || 'bg-stone-50 text-stone-600 border-stone-200')}>{b.status?.replace('_', ' ')}</span></td>
                    <td className="px-5 py-3.5">
                      <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border', 
                        b.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        b.paymentStatus === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        b.paymentStatus === 'unpaid' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                        'bg-stone-100 text-stone-600 border-stone-200'
                      )}>
                        {b.paymentStatus?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right"><span className="font-semibold text-stone-900">{'\u00A3'}{b.amountCharged ?? b.depositAmount}</span></td>
                    <td className="px-5 py-3.5 text-xs text-stone-500 hidden lg:table-cell">
                      {(b.confirmedDate || b.createdAt) && new Date(b.confirmedDate || b.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AnimatePresence>
        {selectedBooking && (
          <BookingDetail 
            bookingId={selectedBooking} 
            onClose={() => setSelectedBooking(null)} 
            onUpdate={() => loadData(true)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}
